import { useState } from 'react';
import clsx from 'clsx';
import type { BodyPart, BodyPartQualifier, BodyPartSide } from '@/domain/types';

export interface PlacementValue {
  bodyPart?: BodyPart;
  bodyPartSide?: BodyPartSide;
  bodyPartQualifier?: BodyPartQualifier;
  placementNote: string;
}

interface Props {
  value: PlacementValue;
  onChange: (patch: Partial<PlacementValue>) => void;
  idPrefix: string;
}

// Mirrors the BodyPart union in domain/types (hand-maintained, so keep both in
// step). Head-down order, not alphabetical, so the list scans like a body.
const BODY_PARTS: BodyPart[] = [
  'head', 'face', 'ear', 'neck', 'chest', 'back', 'shoulder', 'upper-arm', 'bicep', 'forearm',
  'hand', 'torso', 'hip', 'buttocks', 'thigh', 'hamstring', 'shin', 'calf', 'foot',
];
const SIDES: BodyPartSide[] = ['left', 'right'];
const QUALIFIERS: BodyPartQualifier[] = ['inner', 'outer', 'upper', 'lower'];

const chipCls = (active: boolean) =>
  clsx(
    'rounded-full border px-2.5 py-1 text-[11px] capitalize transition',
    active
      ? 'border-gold-bright bg-gold/15 text-gold-bright'
      : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
  );

const inputCls =
  'w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright';

/**
 * Structured placement: body-part select + left/right beside it, qualifier chips
 * below, and an "Other" pill that swaps the select for a free-text box when the
 * part the client wants isn't on the list (sternum, spine, knuckles). Other
 * writes to the same free-text `placement` field the label formatters already
 * fall back to, so nothing new is persisted.
 */
export default function PlacementFields({ value, onChange, idPrefix }: Props) {
  // Records that already carry free text open with Other on, so existing data is
  // never hidden behind a collapsed control.
  const [other, setOther] = useState(() => value.placementNote.trim().length > 0);

  // Turning Other on drops any listed part so the two can never disagree about
  // what the part is; turning it off clears the text it was holding.
  const toggleOther = () => {
    if (other) {
      setOther(false);
      onChange({ placementNote: '' });
    } else {
      setOther(true);
      onChange({ bodyPart: undefined });
    }
  };

  return (
    <div>
      <span className="label-small-caps">Placement</span>
      <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 items-center">
        <select
          value={value.bodyPart ?? ''}
          disabled={other}
          onChange={(e) => onChange({ bodyPart: (e.target.value || undefined) as BodyPart | undefined })}
          className={clsx(inputCls, other && 'opacity-40 cursor-not-allowed')}
          data-testid={`${idPrefix}-bodypart`}
        >
          <option value="">Body part…</option>
          {BODY_PARTS.map((bp) => (
            <option key={bp} value={bp}>
              {bp.replace(/-/g, ' ')}
            </option>
          ))}
        </select>
        {/* Side sits beside the part it modifies, not adrift in the chip row. */}
        <div
          className="inline-flex items-center rounded-md border border-gold-dim/30 overflow-hidden"
          data-testid={`${idPrefix}-side`}
        >
          {SIDES.map((s) => {
            const active = value.bodyPartSide === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => onChange({ bodyPartSide: active ? undefined : s })}
                className={clsx(
                  'px-3 py-2 text-[11px] capitalize transition',
                  active ? 'bg-gold/15 text-gold-bright' : 'text-cream-soft hover:text-gold-bright',
                )}
                data-testid={`${idPrefix}-side-${s}`}
              >
                {s}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {QUALIFIERS.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onChange({ bodyPartQualifier: value.bodyPartQualifier === q ? undefined : q })}
            className={chipCls(value.bodyPartQualifier === q)}
          >
            {q}
          </button>
        ))}
        <span className="mx-1 self-center text-gold-dim/40">·</span>
        <button
          type="button"
          onClick={toggleOther}
          className={chipCls(other)}
          aria-pressed={other}
          data-testid={`${idPrefix}-placement-other`}
        >
          Other
        </button>
      </div>
      {other ? (
        <input
          type="text"
          value={value.placementNote}
          onChange={(e) => onChange({ placementNote: e.target.value })}
          placeholder="Which part - e.g. sternum"
          className={clsx(inputCls, 'mt-2')}
          data-testid={`${idPrefix}-placement-note`}
        />
      ) : null}
    </div>
  );
}
